import AdminUserDrawer from "../components/AdminUserDrawer";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { Users, Layers, Plug, Rocket, ChevronRight, ShieldOff, ShieldCheck, Send, RefreshCw, Trash2, MessageCircle } from "lucide-react";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");

function TelegramPanel() {
  const [st, setSt] = useState(null);
  const [token, setToken] = useState("");
  const [subs, setSubs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/telegram").then((r) => setSt(r.data));
    api.get("/admin/telegram/subscribers").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/telegram/logs").then((r) => setLogs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!token.trim()) return;
    setBusy(true);
    try { await api.post("/admin/telegram/config", { token: token.trim() });
      toast.success("Telegram bot connected"); setToken(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    try { await api.delete("/admin/telegram/config"); toast.success("Disconnected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const sync = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/telegram/sync");
      toast.success(`Synced — ${data.added} new subscriber(s)`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const broadcast = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/telegram/broadcast", { message: msg.trim() });
      toast.success(`Sent to ${data.sent}, failed ${data.failed}`); setMsg(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid="telegram-panel">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="font-heading font-bold flex items-center gap-2"><MessageCircle className="w-4 h-4 text-sky-600" />Telegram Alerts & Broadcast</h3>
        {st?.configured && (
          <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-50 text-emerald-700" data-testid="telegram-connected-badge">
            @{st.bot_username} · {fmt(st.subscriber_count)} subs
          </span>
        )}
      </div>

      {!st?.configured ? (
        <div className="p-6 space-y-3">
          <p className="text-sm text-slate-600">Create a bot with <span className="font-mono">@BotFather</span> on Telegram, then paste the bot token here to send alerts and broadcasts to your users.</p>
          <div className="flex gap-2">
            <input data-testid="telegram-token-input" type="password" value={token} onChange={(e) => setToken(e.target.value)}
              placeholder="123456:ABC-DEF..." className="flex-1 h-10 px-3 border border-slate-300 font-mono text-sm focus:border-slate-900 outline-none" />
            <button data-testid="telegram-connect-btn" onClick={connect} disabled={busy}
              className="h-10 px-4 text-sm font-semibold bg-slate-900 text-white flex items-center gap-2 disabled:opacity-50">
              <Plug className="w-4 h-4" />Connect
            </button>
          </div>
        </div>
      ) : (
        <div className="p-6 space-y-5">
                  
          <div className="flex items-center gap-2">
            <button data-testid="telegram-sync-btn" onClick={sync} disabled={busy}
              className="h-9 px-3 text-xs font-semibold border border-slate-300 flex items-center gap-1.5 hover:border-slate-900 disabled:opacity-50">
              <RefreshCw className="w-3.5 h-3.5" />Sync subscribers
            </button>
            <button data-testid="telegram-disconnect-btn" onClick={disconnect}
              className="h-9 px-3 text-xs font-semibold border border-slate-300 text-rose-600 flex items-center gap-1.5 hover:border-rose-500">
              <Trash2 className="w-3.5 h-3.5" />Disconnect
            </button>
          </div>
          <p className="text-xs text-slate-500">Users must first send any message to <span className="font-mono">@{st.bot_username}</span>, then click <b>Sync subscribers</b>.</p>

          <div>
            <div className="text-xs font-bold uppercase text-slate-500 mb-2">Broadcast message ({fmt(st.subscriber_count)} recipients)</div>
            <textarea data-testid="telegram-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3}
              placeholder="Type an alert or announcement to send to all subscribers…"
              className="w-full px-3 py-2 border border-slate-300 text-sm focus:border-slate-900 outline-none" />
            <button data-testid="telegram-broadcast-btn" onClick={broadcast} disabled={busy || !msg.trim()}
              className="mt-2 h-9 px-4 text-sm font-semibold bg-sky-600 text-white flex items-center gap-2 disabled:opacity-50">
              <Send className="w-4 h-4" />Send broadcast
            </button>
          </div>

          {subs.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase text-slate-500 mb-2">Subscribers ({subs.length})</div>
              <div className="space-y-1 max-h-32 overflow-auto" data-testid="telegram-subscribers">
                {subs.map((s) => (
                  <div key={s.chat_id} className="text-sm flex justify-between border-b border-slate-100 py-1">
                    <span>{s.name || s.username || "—"}</span>
                    <span className="font-mono text-xs text-slate-500">{s.username ? "@" + s.username : s.chat_id}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {logs.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase text-slate-500 mb-2">Recent broadcasts</div>
              <div className="space-y-1 max-h-32 overflow-auto" data-testid="telegram-logs">
                {logs.map((l, i) => (
                  <div key={i} className="text-xs border-b border-slate-100 py-1">
                    <div className="truncate">{l.message}</div>
                    <div className="font-mono text-[10px] text-slate-400">sent {l.sent} · failed {l.failed}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="p-5 bg-white border-r border-b border-slate-200">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold tracking-[0.15em] uppercase text-slate-500">{label}</span>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className="font-mono text-3xl font-medium">{value}</div>
    </div>
  );
}

export default function AdminPanel() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [detail, setDetail] = useState(null);
  const closeUser = () => setDetail(null);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
  };
  useEffect(() => { load(); }, []);

  async function setPaidStrategyAccess(uid, enabled) {
    try {
      await api.patch(`/admin/users/${uid}/paid-strategy-access`, {
        enabled,
      });

      setDetail((d) =>
        d && d.user.id === uid
          ? {
              ...d,
              user: {
                ...d.user,
                special_strategy_access: enabled,
              },
            }
          : d
      );

      toast.success(
        enabled
          ? "Paid strategy access enabled"
          : "Paid strategy access disabled"
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
        "Failed to update paid strategy access"
      );
    }
  }

  const openUser = (uid) => api.get(`/admin/users/${uid}`).then((r) => setDetail(r.data));
  async function approveUser(uid) {
    try {
      await api.post(`/admin/users/${uid}/approve`);
      const response = await api.get(`/admin/users/${uid}`);
      setDetail(response.data);
      load();
      toast.success("Account approved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve account");
    }
  }

  const toggleActive = async (uid) => {
    try { const { data } = await api.post(`/admin/users/${uid}/toggle-active`);
      toast.success(data.disabled ? "User disabled" : "User enabled");
      setDetail((d) => (d && d.user.id === uid ? { ...d, user: { ...d.user, disabled: data.disabled } } : d));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <PageHeader title="Admin Panel" subtitle="Manage users and their data" />
      <div className="p-8">
        <div className="grid grid-cols-2 md:grid-cols-5 border-l border-t border-slate-200 mb-8">
          <Stat label="Users" value={fmt(stats?.users)} icon={Users} />
          <Stat label="Strategies" value={fmt(stats?.strategies)} icon={Layers} />
          <Stat label="Brokers" value={fmt(stats?.broker_connections)} icon={Plug} />
          <Stat label="Deployments" value={fmt(stats?.deployments)} icon={Rocket} />
          <Stat label="Orders" value={fmt(stats?.orders)} icon={Rocket} />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">

          {/* USERS LIST */}
          <div className="border border-slate-200 bg-white">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="font-heading font-bold">Users</h3>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-bold uppercase text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3 text-right">Strat</th>
                  <th className="px-4 py-3 text-right">Brk</th>
                  <th className="px-4 py-3 text-right">Dep</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>

              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    data-testid={`admin-user-${u.id}`}
                    onClick={() => openUser(u.id)}
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{u.email}</div>
                      {u.disabled && (
                        <span className="text-[10px] text-rose-600 font-mono">
                          DISABLED
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] font-bold px-2 py-0.5 ${
                        u.role === "admin"
                          ? "bg-blue-50 text-blue-700"
                          : "bg-slate-100 text-slate-500"
                      }`}>
                        {u.role.toUpperCase()}
                      </span>
                    </td>

                    <td className="px-4 py-2.5 text-right font-mono">
                      {u.strategies}
                    </td>

                    <td className="px-4 py-2.5 text-right font-mono">
                      {u.brokers}
                    </td>

                    <td className="px-4 py-2.5 text-right font-mono">
                      {u.deployments}
                    </td>

                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        data-testid={`open-admin-user-${u.id}`}
                        onClick={async (e) => {
                          e.stopPropagation();

                          try {
                            const { data } = await api.get(
                              `/admin/users/${u.id}`
                            );

                            setDetail(data);
                          } catch (err) {
                            toast.error(
                              err.response?.data?.detail ||
                              "Failed to load user details"
                            );
                          }
                        }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded-md hover:bg-slate-900 hover:text-white hover:border-slate-900"
                      >
                        OPEN
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        <AdminUserDrawer
          detail={detail}
          onClose={closeUser}
          setPaidStrategyAccess={setPaidStrategyAccess}
          toggleActive={toggleActive}
          approveUser={approveUser}
        />

        </div>

        <div className="mt-8">
          <TelegramPanel />
        </div>
        </div>
    </Layout>
  );
}
